import { useQuery } from "@tanstack/react-query"

import { ApiRequestError } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"
import { useDevAuth } from "@/hooks/use-dev-auth"

import { fetchCompanyUsers, fetchCurrentUser, fetchUsers, fetchUsersWithEmails } from "./api"

type CurrentUserQueryOptions = {
  staleTime?: number
  enabled?: boolean
}

export function useFetchCurrentUser(options?: CurrentUserQueryOptions) {
  const getContext = useApiContext()
  const { userId, orgId, isLoaded } = useDevAuth()

  return useQuery({
    queryKey: ["user", "me", userId, orgId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchCurrentUser(context)
    },
    staleTime: options?.staleTime ?? 1000 * 60 * 10,
    // Let the redirect page claim pending invitations before retrying user lookup.
    retry: (failureCount, error) => !(error instanceof ApiRequestError && error.status === 401) && failureCount < 3,
    enabled: (options?.enabled ?? true) && isLoaded,
  })
}

export function useFetchUsers(bookIdOverride?: string) {
  const urlBookId = useBookId()
  const bookId = bookIdOverride ?? urlBookId
  const getContext = useApiContext(bookIdOverride)
  return useQuery({
    queryKey: ["users", bookId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchUsers(context)
    },
    enabled: !!bookId,
    staleTime: 1000 * 60 * 10,
  })
}

export function useFetchUsersWithEmails() {
  const bookId = useBookId()
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["users", bookId, "with-emails"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchUsersWithEmails(context)
    },
    enabled: !!bookId,
    staleTime: 1000 * 60 * 10,
  })
}

export function useFetchCompanyUsers(options?: { enabled?: boolean }) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["company-users"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchCompanyUsers(context)
    },
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 10,
  })
}
